/* =============================================================
   PI Blog Hub — Behaviour for the blog landing and article sections
   <pi-bh-nav>        mobile topic dropdown
   <pi-bh-tabs>       Popular Articles tabs
   <pi-bh-show-more>  progressive "Show more" for article grids
   <pi-bh-carousel>   dots, arrows and mouse drag for the article carousel
   <pi-bh-sort>       Latest / Most popular menu for the article section
   <pi-bh-article>    article table of contents
   ============================================================= */

if (!customElements.get('pi-bh-nav')) {
  customElements.define(
    'pi-bh-nav',
    class PiBhNav extends HTMLElement {
      constructor() {
        super();
        this.initArrows();
        this.initToggle();
      }

      // Above 1280px the topics sit in one scrolling row; arrows show only in the
      // direction there is more to see.
      initArrows() {
        this.list = this.querySelector('.pi-bh-nav__links');
        this.prev = this.querySelector('[data-nav-prev]');
        this.next = this.querySelector('[data-nav-next]');
        if (!this.list || !this.prev || !this.next) return;

        this.updateArrows = this.updateArrows.bind(this);
        this.prev.addEventListener('click', () => this.scrollList(-1));
        this.next.addEventListener('click', () => this.scrollList(1));
        this.list.addEventListener('scroll', this.updateArrows, { passive: true });

        // Watch the items too: their widths change without the list resizing, e.g.
        // when a deferred script swaps in another web font after first interaction.
        const observer = new ResizeObserver(this.updateArrows);
        observer.observe(this.list);
        Array.from(this.list.children).forEach((item) => observer.observe(item));
      }

      scrollList(direction) {
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        this.list.scrollBy({ left: direction * this.list.clientWidth * 0.75, behavior: reduceMotion ? 'auto' : 'smooth' });
      }

      updateArrows() {
        const { scrollLeft, scrollWidth, clientWidth } = this.list;
        const overflowing = scrollWidth - clientWidth > 1;
        // Read focus first: hiding the focused arrow moves focus to the page.
        const focused = document.activeElement;
        this.prev.hidden = !overflowing || scrollLeft <= 1;
        this.next.hidden = !overflowing || scrollLeft + clientWidth >= scrollWidth - 1;

        // Don't strand keyboard focus on an arrow that just disappeared.
        if (focused === this.next && this.next.hidden && !this.prev.hidden) this.prev.focus();
        if (focused === this.prev && this.prev.hidden && !this.next.hidden) this.next.focus();
      }

      initToggle() {
        this.toggle = this.querySelector('[data-nav-toggle]');
        if (!this.toggle) return;

        this.toggle.addEventListener('click', () => this.setOpen(!this.hasAttribute('data-open')));

        // The open list overlays the page, so close it on Escape or an outside click.
        this.addEventListener('keydown', (event) => {
          if (event.key !== 'Escape' || !this.hasAttribute('data-open')) return;
          this.setOpen(false);
          this.toggle.focus();
        });
        document.addEventListener('click', (event) => {
          if (this.hasAttribute('data-open') && !this.contains(event.target)) this.setOpen(false);
        });
      }

      setOpen(open) {
        if (!this.toggle) return;
        this.toggleAttribute('data-open', open);
        this.toggle.setAttribute('aria-expanded', String(open));
      }
    }
  );
}

if (!customElements.get('pi-bh-tabs')) {
  customElements.define(
    'pi-bh-tabs',
    class PiBhTabs extends HTMLElement {
      constructor() {
        super();
        this.tabs = Array.from(this.querySelectorAll('[role="tab"]'));

        this.tabs.forEach((tab) => {
          tab.addEventListener('click', () => this.select(tab));
          tab.addEventListener('keydown', this.onKeydown.bind(this));
        });
      }

      select(tab, focus = false) {
        this.tabs.forEach((item) => {
          const selected = item === tab;
          item.setAttribute('aria-selected', String(selected));
          item.tabIndex = selected ? 0 : -1;

          const panel = document.getElementById(item.getAttribute('aria-controls'));
          if (panel) panel.hidden = !selected;
        });

        if (focus) tab.focus();
      }

      onKeydown(event) {
        const index = this.tabs.indexOf(event.currentTarget);
        const count = this.tabs.length;
        let next;

        switch (event.key) {
          case 'ArrowRight':
            next = this.tabs[(index + 1) % count];
            break;
          case 'ArrowLeft':
            next = this.tabs[(index - 1 + count) % count];
            break;
          case 'Home':
            next = this.tabs[0];
            break;
          case 'End':
            next = this.tabs[count - 1];
            break;
          default:
            return;
        }

        event.preventDefault();
        this.select(next, true);
      }
    }
  );
}

if (!customElements.get('pi-bh-show-more')) {
  customElements.define(
    'pi-bh-show-more',
    class PiBhShowMore extends HTMLElement {
      constructor() {
        super();
        this.button = this.querySelector('[data-show-more]');
        if (this.button) this.button.addEventListener('click', this.reveal.bind(this));
      }

      reveal() {
        const step = parseInt(this.dataset.step, 10) || 3;
        // data-excluded cards belong to the other sort order (see <pi-bh-sort>).
        const hidden = Array.from(this.querySelectorAll('[data-card][hidden]:not([data-excluded])'));
        const batch = hidden.slice(0, step);

        batch.forEach((card) => card.removeAttribute('hidden'));
        if (hidden.length <= step) this.button.parentElement.hidden = true;

        // Keep keyboard users in place: focus the first newly revealed article.
        const firstLink = batch[0] && batch[0].querySelector('a');
        if (firstLink) firstLink.focus();
      }
    }
  );
}

if (!customElements.get('pi-bh-carousel')) {
  // Settling decelerates into place; arrows and dots speed up then slow down.
  const easeOutCubic = (t) => 1 - Math.pow(1 - t, 3);
  const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2);

  customElements.define(
    'pi-bh-carousel',
    // Works like the P2 feature carousel (sections/pi-feature-carousel.liquid): one dot
    // per card, the card at the left gutter is current, and a mouse can drag the row.
    class PiBhCarousel extends HTMLElement {
      constructor() {
        super();
        this.track = this.querySelector('[data-carousel-track]');
        this.controls = this.querySelector('[data-carousel-controls]');
        this.dotList = this.querySelector('[data-carousel-dots]');
        this.prev = this.querySelector('[data-carousel-prev]');
        this.next = this.querySelector('[data-carousel-next]');
        if (!this.track || !this.controls) return;

        this.cards = this.readCards();
        this.dots = Array.from(this.dotList.querySelectorAll('.pi-bh-carousel__dot'));
        this.progressive = this.dotList.hasAttribute('data-progressive');
        this.loop = this.hasAttribute('data-loop');
        this.current = 0;
        this.target = null; // card a click is scrolling to; holds the highlight until it gets there
        this.perPage = 1;

        this.dots.forEach((dot, index) => dot.addEventListener('click', () => this.goTo(index)));
        if (this.prev) this.prev.addEventListener('click', () => this.step(-1));
        if (this.next) this.next.addEventListener('click', () => this.step(1));

        // Mice and trackpads get eased settling instead of native snapping, which on a
        // trackpad cuts the swipe's momentum short and jumps (see the CSS). Touch
        // screens keep native snapping, which feels right there.
        this.finePointer = window.matchMedia('(hover: hover) and (pointer: fine)');
        this.direction = 0;
        this.lastLeft = 0;

        this.track.addEventListener('scroll', () => {
          if (!this.frame) this.frame = requestAnimationFrame(() => this.update());
          const delta = this.track.scrollLeft - this.lastLeft;
          this.lastLeft = this.track.scrollLeft;
          if (this.animating || this.dragging) return;
          if (Math.abs(delta) > 0.5) this.direction = Math.sign(delta);

          // Let the gesture and its momentum play out, then ease to a card.
          if (!this.finePointer.matches) return;
          clearTimeout(this.settleTimer);
          this.settleTimer = setTimeout(() => this.settle(), 140);
        }, { passive: true });
        this.track.addEventListener('scrollend', () => {
          if (this.animating) return; // our own animation ends itself
          this.release();
          this.update();
        });
        // A swipe or wheel takes over from a click that's still scrolling.
        const interrupt = () => {
          this.stopAnimation();
          this.release();
        };
        this.track.addEventListener('wheel', interrupt, { passive: true });
        this.track.addEventListener('touchstart', interrupt, { passive: true });

        // Cards per page change with the CSS breakpoints, so re-measure on resize.
        new ResizeObserver(() => this.layout()).observe(this.track);

        this.initDrag();
        if (this.loop) this.initSwipeWrap();
        if (this.progressive) this.updateDotWindow();
      }

      layout() {
        const [first, second] = this.cards;
        if (!first) return;

        // Arrows move a page: the cards that fit in the row at this breakpoint.
        const style = getComputedStyle(this.track);
        const inner = this.track.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
        const gap = parseFloat(style.columnGap) || 0;
        const step = second ? second.offsetLeft - first.offsetLeft : inner + gap;
        this.perPage = Math.max(1, Math.round((inner + gap) / step));

        this.update();

        // With many articles the dots outgrow a phone screen; then only swiping
        // (and the arrows, if shown) remain.
        this.dotList.hidden = false;
        this.dotList.hidden = this.controls.scrollWidth > this.controls.clientWidth + 1;
      }

      // Scroll position that shows each card at the left gutter. The last cards
      // can't reach it; they stop at the end of the row.
      positions() {
        const max = Math.max(0, this.track.scrollWidth - this.track.clientWidth);
        return this.cards.map((card) => Math.min(card.offsetLeft - this.cards[0].offsetLeft, max));
      }

      // Card to settle on after a swipe or fling: the next one in the direction of
      // travel once the row is a fifth of the way there, so short swipes still
      // advance rather than springing back.
      snapIndex(left, direction) {
        const positions = this.positions();
        let before = 0;
        positions.forEach((position, index) => {
          if (position <= left + 1 && position > positions[before]) before = index;
        });
        const after = positions.findIndex((position) => position > left + 1);
        if (after === -1) return before;

        const progress = (left - positions[before]) / (positions[after] - positions[before]);
        const threshold = direction > 0 ? 0.2 : direction < 0 ? 0.8 : 0.5;
        return progress > threshold ? after : before;
      }

      settle() {
        if (this.animating || this.dragging) return;
        this.goTo(this.snapIndex(this.track.scrollLeft, this.direction), 450, easeOutCubic);
      }

      // Nearest card to a scroll position; ties go to the earlier card.
      indexAt(left) {
        const positions = this.positions();
        let best = 0;
        positions.forEach((position, index) => {
          if (Math.abs(position - left) < Math.abs(positions[best] - left) - 1) best = index;
        });
        return best;
      }

      setActive(index) {
        this.current = index;
        this.dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === index)));
        if (this.progressive) this.updateDotWindow();
      }

      // Graduated dots: five at a time, centred on the current dot where possible.
      // The current dot and its neighbours are full size; towards an edge with more
      // dots beyond it they shrink to 0.75 then 0.5, and the rest are hidden.
      updateDotWindow() {
        const count = this.dots.length;
        const slots = Math.min(count, 5);
        const start = Math.min(Math.max(this.current - 2, 0), count - slots);
        const end = start + slots - 1;
        this.dotList.style.setProperty('--pi-bh-dot-slots', slots);
        this.dotList.style.setProperty('--pi-bh-dot-start', start);

        this.dots.forEach((dot, i) => {
          let scale = 1;
          if (i < start || i > end) {
            scale = 0;
          } else if (Math.abs(i - this.current) > 1) {
            const after = i > this.current;
            const fromEdge = after ? end - i : i - start;
            const moreBeyond = after ? end < count - 1 : start > 0;
            if (moreBeyond && fromEdge < 2) scale = fromEdge === 0 ? 0.5 : 0.75;
          }
          dot.style.setProperty('--pi-bh-dot-scale', scale);

          // Hidden dots stay out of the tab order: focusing one would scroll the
          // clipped window and undo its position.
          dot.tabIndex = scale ? 0 : -1;
          if (scale) dot.removeAttribute('aria-hidden');
          else dot.setAttribute('aria-hidden', 'true');
        });
      }

      update() {
        this.frame = 0;
        const { scrollLeft, scrollWidth, clientWidth } = this.track;
        const max = scrollWidth - clientWidth;
        this.atStart = scrollLeft <= 1;
        this.atEnd = scrollLeft >= max - 1;
        this.controls.hidden = max <= 1;

        if (this.prev) {
          // A looping carousel's arrows never run out.
          // Read focus first: disabling the focused arrow moves focus to the page.
          const focused = document.activeElement;
          this.prev.disabled = !this.loop && this.atStart;
          this.next.disabled = !this.loop && this.atEnd;
          if (focused === this.next && this.next.disabled && !this.prev.disabled) this.prev.focus();
          if (focused === this.prev && this.prev.disabled && !this.next.disabled) this.next.focus();
        }

        if (this.target !== null) {
          if (Math.abs(scrollLeft - this.positions()[this.target]) > 1) return;
          this.target = null;
        }
        // The last few cards share the end position; keep a clicked one highlighted
        // there rather than handing it to the first card that reaches the end.
        const positions = this.positions();
        const index = this.indexAt(scrollLeft);
        if (Math.abs(positions[index] - positions[this.current]) > 1) this.setActive(index);
      }

      step(direction) {
        if (this.loop && direction > 0 && this.atEnd) return this.goTo(0);
        if (this.loop && direction < 0 && this.atStart) return this.goTo(this.cards.length - 1);
        this.goTo(this.current + direction * this.perPage);
      }

      // Arrows and dots: 0.3s for one card up to 0.7s for long jumps, easing in and out.
      goTo(index, duration, easing = easeInOutCubic) {
        index = Math.max(0, Math.min(this.cards.length - 1, index));
        const left = this.positions()[index];
        const distance = Math.abs(this.track.scrollLeft - left);
        this.setActive(index);
        this.target = distance > 1 ? index : null;
        this.animateTo(left, duration || Math.min(700, 300 + distance * 0.25), easing);
      }

      // Native smooth scrolling is short, can't be slowed and fights snap points, so
      // the row is animated here. Snapping is off while it runs (.is-animating).
      animateTo(left, duration, easing) {
        this.stopAnimation();
        const from = this.track.scrollLeft;
        const distance = left - from;
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        if (Math.abs(distance) < 1 || reduceMotion) {
          this.track.scrollLeft = left;
          this.release();
          this.update();
          return;
        }

        this.animating = true;
        this.track.classList.add('is-animating');
        const start = performance.now();
        const tick = (now) => {
          const progress = Math.min(1, (now - start) / duration);
          this.track.scrollLeft = from + distance * easing(progress);
          if (progress < 1) {
            this.animation = requestAnimationFrame(tick);
            return;
          }
          this.stopAnimation();
          this.release();
          this.update();
        };
        this.animation = requestAnimationFrame(tick);
      }

      stopAnimation() {
        if (!this.animating) return;
        cancelAnimationFrame(this.animation);
        this.animating = false;
        this.track.classList.remove('is-animating');
      }

      release() {
        this.target = null;
      }

      // Cards of the other sort order stay in the track, hidden (see <pi-bh-sort>).
      readCards() {
        return Array.from(this.track.children).filter((card) => !card.hasAttribute('data-excluded'));
      }

      // After <pi-bh-sort> reorders the cards: read them again and start over at the
      // first. Both orders show the same number of cards, so the dots stay as they are.
      refresh() {
        this.stopAnimation();
        this.release();
        clearTimeout(this.settleTimer);
        this.cards = this.readCards();
        this.track.scrollLeft = 0;
        this.lastLeft = 0;
        this.current = -1;
        this.setActive(0);
        this.layout();
      }

      // Mouse drag with a fling: a quick release carries on in that direction and
      // eases to a card. Touch and trackpads already scroll natively.
      initDrag() {
        let drag = null;
        let justDragged = false;

        const end = () => {
          if (!drag) return;
          const { moved, samples } = drag;
          drag = null;
          this.dragging = false;
          if (!moved) return;
          this.track.classList.remove('is-dragging');
          justDragged = true;
          setTimeout(() => { justDragged = false; }, 0);

          // Pointer speed over the last 100ms, in px per ms (0 if the mouse stopped).
          const now = performance.now();
          const recent = samples.filter((sample) => now - sample.time < 100);
          const first = recent[0];
          const last = recent[recent.length - 1];
          const velocity = first && last.time > first.time ? (last.x - first.x) / (last.time - first.time) : 0;

          // Where the row would coast to in about a quarter of a second.
          const projected = this.track.scrollLeft - velocity * 250;
          const direction = Math.abs(velocity) > 0.1 ? -Math.sign(velocity) : 0;
          this.goTo(this.snapIndex(projected, direction), Math.abs(velocity) > 0.1 ? 550 : 400, easeOutCubic);
        };

        this.track.addEventListener('dragstart', (event) => event.preventDefault());
        this.track.addEventListener('pointerdown', (event) => {
          if (event.pointerType !== 'mouse' || event.button !== 0) return;
          this.stopAnimation();
          this.release();
          clearTimeout(this.settleTimer);
          drag = { x: event.clientX, left: this.track.scrollLeft, moved: false, samples: [] };
        });
        window.addEventListener('pointermove', (event) => {
          if (!drag) return;
          if (!(event.buttons & 1)) return end(); // the button was released outside the window
          const distance = event.clientX - drag.x;
          if (!drag.moved && Math.abs(distance) < 5) return;
          drag.moved = true;
          this.dragging = true;
          this.track.classList.add('is-dragging');
          this.track.scrollLeft = drag.left - distance;
          drag.samples.push({ x: event.clientX, time: performance.now() });
          if (drag.samples.length > 20) drag.samples.shift();
        });
        window.addEventListener('pointerup', end);
        window.addEventListener('pointercancel', end);

        // A drag shouldn't also count as a click on the card under the cursor.
        this.track.addEventListener('click', (event) => {
          if (!justDragged) return;
          event.preventDefault();
          event.stopPropagation();
        }, true);
      }

      // The track can't scroll past its ends, so a swipe that starts at an end
      // and pushes further wraps to the other end.
      initSwipeWrap() {
        this.track.addEventListener('touchstart', (event) => {
          this.swipe = { x: event.touches[0].clientX, atStart: this.atStart, atEnd: this.atEnd };
        }, { passive: true });
        this.track.addEventListener('touchend', (event) => {
          if (!this.swipe) return;
          const distance = event.changedTouches[0].clientX - this.swipe.x;
          if (this.swipe.atEnd && distance < -40) this.goTo(0);
          if (this.swipe.atStart && distance > 40) this.goTo(this.cards.length - 1);
          this.swipe = null;
        }, { passive: true });
      }
    }
  );
}

if (!customElements.get('pi-bh-sort')) {
  customElements.define(
    'pi-bh-sort',
    // The server renders the articles of both orders once, newest first: each card
    // carries data-rank-latest and data-rank-popular, and the cards outside the
    // current order carry data-excluded. Switching shows that order's hero and
    // reorders the cards.
    class PiBhSort extends HTMLElement {
      constructor() {
        super();
        this.select = this.querySelector('[data-sort-select]');
        this.status = this.querySelector('[data-sort-status]');
        if (!this.select) return;

        this.select.addEventListener('change', () => {
          this.apply(this.select.value, true);
          this.fit();
        });
        // The page arrives in Latest order; follow the menu if the browser kept another choice.
        if (this.select.value !== 'latest') this.apply(this.select.value);

        // Re-measure once the web font has loaded and when the phone size applies.
        this.fit();
        if (document.fonts) document.fonts.ready.then(() => this.fit());
        window.matchMedia('(max-width: 575px)').addEventListener('change', () => this.fit());
      }

      // A select is as wide as its longest option, which would leave a gap between
      // "Latest" and the chevron. Size it to the chosen option, as in Figma.
      fit() {
        const style = getComputedStyle(this.select);
        const probe = document.createElement('span');
        probe.textContent = this.select.selectedOptions[0].text;
        Object.assign(probe.style, {
          position: 'absolute',
          visibility: 'hidden',
          whiteSpace: 'nowrap',
          fontFamily: style.fontFamily,
          fontSize: style.fontSize,
          fontWeight: style.fontWeight,
          letterSpacing: style.letterSpacing,
        });
        this.append(probe);
        const text = probe.getBoundingClientRect().width;
        probe.remove();
        this.select.style.width = `${Math.ceil(text + parseFloat(style.paddingLeft) + parseFloat(style.paddingRight))}px`;
      }

      apply(order, announce = false) {
        const rank = order === 'popular' ? 'rankPopular' : 'rankLatest';
        const first = Number(this.dataset.first) || 0; // 1 when the hero takes the top article
        const windowSize = Number(this.dataset.window) || 0;
        const initial = Number(this.dataset.initial) || windowSize;

        this.querySelectorAll('[data-hero]').forEach((hero) => {
          hero.hidden = !hero.dataset.hero.split(' ').includes(order);
        });

        const cards = Array.from(this.querySelectorAll('[data-card]'));
        if (cards.length) {
          const list = cards[0].parentElement;
          cards.sort((a, b) => Number(a.dataset[rank]) - Number(b.dataset[rank]));
          cards.forEach((card) => {
            const position = Number(card.dataset[rank]) - first;
            const shown = position >= 0 && position < windowSize;
            card.toggleAttribute('data-excluded', !shown);
            card.hidden = shown && position >= initial; // grid: past "Show more"
            list.append(card);
          });
        }

        // The grid's "Show more" starts over; the row and the carousel go back to the start.
        const more = this.querySelector('[data-show-more]');
        if (more) more.parentElement.hidden = !cards.some((card) => card.hidden && !card.hasAttribute('data-excluded'));
        const row = this.querySelector('.pi-bh-track--scroll');
        if (row) row.scrollLeft = 0;
        const carousel = this.querySelector('pi-bh-carousel');
        if (carousel && carousel.refresh) carousel.refresh();

        if (announce && this.status) this.status.textContent = `Sorted by ${this.select.selectedOptions[0].text}`;
      }
    }
  );
}

if (!customElements.get('pi-bh-article')) {
  customElements.define(
    'pi-bh-article',
    class PiBhArticle extends HTMLElement {
      constructor() {
        super();
        // Matches the CSS breakpoint where the TOC becomes a sidebar.
        this.desktop = window.matchMedia('(min-width: 1025px)');
        this.card = this.querySelector('.pi-bh-toc__card');
        this.header = this.querySelector('[data-toc-header]');
        this.tab = this.querySelector('[data-toc-tab]');
        this.targets = Array.from(this.querySelectorAll('.pi-bh-toc__link'))
          .map((link) => ({ link, heading: document.getElementById(decodeURIComponent(link.hash.slice(1))) }))
          .filter((target) => target.heading);

        this.onScroll = this.onScroll.bind(this);
        this.measureHeader = this.measureHeader.bind(this);
        if (!this.header) return;

        this.header.addEventListener('click', () => this.toggle());
        if (this.tab) this.tab.addEventListener('click', () => this.toggle());
        this.desktop.addEventListener('change', () => this.sync());
        this.targets.forEach(({ link, heading }) => {
          link.addEventListener('click', (event) => this.goTo(event, link, heading));
        });
        this.sync();
      }

      connectedCallback() {
        window.addEventListener('scroll', this.onScroll, { passive: true });
        window.addEventListener('resize', this.measureHeader);
        this.measureHeader();
        this.updateActive();
      }

      disconnectedCallback() {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.measureHeader);
      }

      // Desktop starts expanded and collapses to the edge tab; mobile starts collapsed.
      isOpen() {
        return this.desktop.matches ? !this.hasAttribute('data-toc-collapsed') : this.hasAttribute('data-toc-open');
      }

      toggle() {
        const desktop = this.desktop.matches;

        // Desktop: the sidebar column only animates while this flag is set (see CSS).
        if (desktop) {
          this.setAttribute('data-toc-animating', '');
          clearTimeout(this.animationTimer);
          this.animationTimer = setTimeout(() => this.removeAttribute('data-toc-animating'), 700);
        }

        this.toggleAttribute(desktop ? 'data-toc-collapsed' : 'data-toc-open');
        this.sync();

        // On desktop the clicked button is hidden, so move focus to the one that replaced it.
        if (desktop) (this.isOpen() ? this.header : this.tab).focus({ preventScroll: true });
      }

      sync() {
        this.header.setAttribute('aria-expanded', String(this.isOpen()));
      }

      goTo(event, link, heading) {
        event.preventDefault();
        const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
        heading.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'start' });
        heading.setAttribute('tabindex', '-1');
        heading.focus({ preventScroll: true });
        history.replaceState(null, '', link.hash);
      }

      // The sticky TOC sits below the theme header whenever that header is showing.
      headerOffset() {
        const header = document.getElementById('shopify-section-header');
        if (!header) return 0;
        const showing = header.classList.contains('shopify-section-header-sticky') &&
          !header.classList.contains('shopify-section-header-hidden');
        return showing ? header.offsetHeight : 0;
      }

      measureHeader() {
        const header = document.getElementById('shopify-section-header');
        this.style.setProperty('--pi-bh-header-h', `${header ? header.offsetHeight : 0}px`);
      }

      onScroll() {
        if (this.ticking) return;
        this.ticking = true;
        requestAnimationFrame(() => {
          this.ticking = false;
          this.updateActive();
        });
      }

      // Highlight the last heading that has scrolled past the top of the viewport.
      updateActive() {
        if (!this.targets.length) return;
        const line = this.headerOffset() + 120;
        let active = this.targets[0].link;
        this.targets.forEach(({ link, heading }) => {
          if (heading.getBoundingClientRect().top <= line) active = link;
        });
        if (active === this.active) return;

        if (this.active) this.active.removeAttribute('aria-current');
        active.setAttribute('aria-current', 'location');
        this.active = active;

        // Keep the active link in view when the sidebar list is taller than the screen.
        if (this.desktop.matches && this.card.scrollHeight > this.card.clientHeight) {
          const cardRect = this.card.getBoundingClientRect();
          const linkRect = active.getBoundingClientRect();
          if (linkRect.top < cardRect.top || linkRect.bottom > cardRect.bottom) {
            this.card.scrollTop += linkRect.top - cardRect.top - cardRect.height / 2;
          }
        }
      }
    }
  );
}

// Each section includes this script, so bind document listeners only once.
if (!window.piBlogHubEditorEvents) {
  window.piBlogHubEditorEvents = true;

  // Theme editor: selecting a tab or topic block shows it in the preview.
  document.addEventListener('shopify:block:select', (event) => {
    const target = event.target;

    if (target.matches('pi-bh-tabs [role="tab"]')) {
      target.closest('pi-bh-tabs').select(target);
    }

    const nav = target.closest('pi-bh-nav');
    if (nav) nav.setOpen(true);
    if (target.closest('.pi-bh-nav__links')) target.scrollIntoView({ block: 'nearest', inline: 'nearest' });
  });
}
